<template lang="pug">
  div
    VeeForm(
      ref="form"
      :validation-schema="formSchema"
      @submit="submitForm"
      :class="[formItemClass,'form-item pt-0 lg:pt-ds-md']"
    )
      div(class="block lg:hidden m-0 md:mb-ds-md w-full")
        AvailabilityRemark(
          v-if="hasAvailabilityRemark"
          :block="block")

      div(
        v-if="hasItemIdentifier"
        :class="['w-full order-2 px-ds-md md:pr-ds-md mt-ds-ml md:mt-ds-sm md:mb-ds-md']")
        LazyItemIdentifier(
          :block="block.innerBlocks[0]"
          :lazy="false")

      div(
        v-if="hasVoiceToText"
        :class="['w-full order-2 px-ds-md md:pr-ds-md mt-ds-ml md:mt-ds-sm md:mb-ds-md']")
        LazyVoiceIdentifier(
          :block="block.innerBlocks[0]"
          :lazy="false")

      div(:class="['w-full md:w-6/12 order-2 px-ds-md md:pr-ds-sm', isCtaForm ? 'mt-ds-md md:mt-0' : 'mt-ds-ml md:mt-ds-sm']")
        template(v-if="isCtaForm")
          a(
            class="text-white hover:text-white"
            :href="extractHref(ctaFormButtons[0].innerHtml)"
            style="text-decoration: none;")
            Btn(
              kind="rebrand-cta"
              :block="true"
              :wp-block="ctaFormButtons[0]"
              btn-with-padding
              size="xl")
              .text-rebrandSm {{ getInnerText(ctaFormButtons[0].innerHtml) }}
        template(v-else)
          div(ref="pickupInputWrapper")
            NewLocationPickupInput(
              :delivery-input="$refs.deliveryInput"
              ref="pickupInput"
              :label-position="hasItemIdentifier ? 'animated' : 'border'"
              :block="locationBlock"
              :lazy="false")

      div(:class="['w-full md:w-6/12 order-4 md:order-3 px-ds-md md:pl-ds-sm', isCtaForm ? 'mt-ds-md md:mt-0' : 'mt-ds-ml md:mt-ds-sm']")
        template(v-if="isCtaForm")
          template(v-if="ctaFormHasMultipleButtons")
            a(
              class="text-white hover:text-white"
              :href="extractHref(ctaFormButtons[1].innerHtml)"
              style="text-decoration: none;")
              Btn(
                class="ml-0 text-black70 border border-black20 hover:bg-black10"
                kind="rebrand-cta"
                size="xl"
                btn-with-padding
                :block="true")
                .text-rebrandSm {{ getInnerText(ctaFormButtons[1].innerHtml) }}
          template(v-else)
            PhoneNumber(
              class="call-us-container call-us-cta flex justify-center align-center w-full h-ds-xxl text-black70"
              :showIcon="true"
              :inheritColor="true"
              :showButtonText="true"
              :handleClick="() => handleCallUsClick('cta_form_button')"
              :buttonText="getPhoneNumberValue.formatted")
            div(class="call-us-description lg:hidden") {{ $t('location_form.tap_to_call') }}
        template(v-else)
          div(ref="deliveryInputWrapper")
            NewLocationDeliveryInput(
              ref="deliveryInput"
              :block="locationBlock"
              :label-position="hasItemIdentifier ? 'animated' : 'border'"
              :lazy="false"
              @focus="scrollInputToTop('delivery')")

      div(
        v-if="!isCtaForm && $i18n.locale === 'en' && !hasItemIdentifier && !hasVoiceToText"
        :class="{'w-full mt-ds-ml order-5 px-ds-md self-end': true, 'disabled-div': !isValidForm, 'lg:hidden': !hasExtension}")
        div(
          @click="tryToOpenFurnitureItemsModal"
          class="px-ds-md relative flex justify-between lg:justify-start items-center h-ds-xxl border border-anyvan-grey-dark rounded-md cursor-pointer text-gray-800")
          template(v-if="getItemsCount > 0")
            div(class="-ml-ds-xxxs mr-ds-md mb-ds-sm hidden lg:block")
              Icon.text-blue-primary(
                name="i:search-find"
                :size="24")
            .text-lg.flex-1 {{getItemsCount}} item#[span(v-if="getItemsCount > 1") s] selected
            Icon(
              class="lg:text-blue-primary"
              name="i:pencil-edit"
              :size="20")
          template(v-else)
            div(class="text-lg lg:hidden") What are you moving?
            div(class="lg:hidden")
              Icon(
                name="i:chevron-right-rounded"
                :size="16")
            div(class="-ml-ds-xxxs mr-ds-md mb-ds-sm hidden lg:block")
              Icon.text-blue-primary(
                name="i:search-find"
                :size="24")
            div(class="flex-1 text-lg hidden lg:block") Search for your items
          LazyFurnitureItemsDesktop(
            v-if="!isSmallOrMedium && showFurnitureItems && !hasItemIdentifier && !hasVoiceToText"
            :item-categories="itemCategories"
            @close="hideItems"
            v-click-outside-element="hideItemsOnClickOutside")
        TextInputError.mt-ds-sm(v-if="submittedForm && getInventoryComputed && !getItemsCount")
          | Enter your items

      div(class="flex justify-between w-full flex-col-reverse md:flex-row order-6 p-ds-md")
        FormFooterUK(
          v-if="$i18n.locale === 'en'"
          :trustpilot-customers="formattedTrustpilotCustomers"
          :trustpilot-rating="formattedTrustpilotRating")

        template(v-if="hasCallUs && !isCtaForm")
          PhoneNumber(
            class="call-us-container mt-ds-md md:hidden"
            :showIcon="true"
            :showButtonText="true"
            :handleClick="handleCallUsClick"
            :buttonText="$t('location_form.call_us')")
        div(:class="!isCtaForm ? 'text-center md:flex md:flex-row-reverse md:flex-wrap md:gap-ds-md md:mb-0 md:p-0' : ''")
          div(class="av-column av-column-container av-buttons")
            Btn(
              v-show="!hasItemsDesktop && !isCtaForm"
              kind="rebrand-cta"
              type="submit"
              @click="onCTAClick"
              :block="true"
              :wp-block="ctaBlock.innerBlocks[1].innerBlocks[0].innerBlocks[0]"
              size="xl"
              :disabled="submittingForm"
              :loading="submittingForm")
              .text-rebrandSm {{ getInnerText(ctaBlock.innerBlocks[1]?.innerBlocks[0]?.innerBlocks[0]?.innerHtml) }}
          div(class="hidden md:block")
            button(
              v-show="hasRetrieveQuote && !hasItemsDesktop"
              type="button"
              @click.prevent="onEmailFormOpen"
              class="av-button-link text-anyvan-cta-secondary"
            ) {{ $t("location_form.already_received_quote") }}

    div(v-if="$i18n.locale === 'es'")
      FormFooter(
        :block="block"
        :trustpilot-rating="formattedTrustpilotRating"
      )

    div(class="md:hidden w-full mt-ds-sm")
      button(
        v-show="hasRetrieveQuote"
        @click.prevent="onEmailFormOpen"
        class="av-button-link text-white"
      ) {{ $t("location_form.already_received_quote") }}

    div(class="hidden lg:block w-full")
      AvailabilityRemark(v-if="hasAvailabilityRemark" :block="block")
    RetrieveQuoteModal(v-if="hasRetrieveQuote && emailFormShow" @close-modal="onEmailFormClose")
    SlideRightTransition
      LazyFurnitureItemsModal(
        v-if="showFurnitureItems && isSmallOrMedium && !hasItemIdentifier && !hasVoiceToText"
        :item-categories="itemCategories"
        @close-modal="showFurnitureItems = false"
        @submit="submitForm(false)")
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { mapActions, mapGetters } from "vuex"
import type { ValidationResult } from "vee-validate"
import { toTypedSchema } from "@vee-validate/yup"
import { string, object } from "yup"
import axios from "axios"
import { patternCheck } from "~/utils/block"
import { formatNumberWithLocale } from "~/utils/formatNumberWithLocale"
import {
  GET_LISTING,
  GET_ITEMS_COUNT,
  SET_DELIVERY_PROPERTY_TYPE,
  SET_PICKUP_PROPERTY_TYPE
} from "~/store/listing"
import {
  ListingService,
  getV4Url,
  getV4UrlByCategoryId,
  getV4ListingPayload,
  addPreListingIdToSessionStorage,
  isValidLocation
} from "~/services/public/Listing"
import {
  AVAILABILITY_REMARK,
  CALL_US,
  ITEM_IDENTIFIER,
  VOICE_TO_TEXT
} from "~/constants/blocks"
import { redirectToV2 } from "~/utils/v4-redirect"
import { GET_PHONE_NUMBER_VALUE } from "~/store/phone-number"
import {
  SET_INVENTORY,
  SET_WINDOW_WIDTH,
  GET_INVENTORY_DESKTOP,
  GET_IS_SMALL_OR_MEDIUM,
  GET_WINDOW_WIDTH
} from "~/store/pages"
import { extractHref, getInnerText } from "~/scripts/useful-functions"
import { ItemService } from "~/services/public/ItemService"
import { CATEGORY_V4_ID_FURNITURE } from "~/constants/category"
import { LOCALE } from "~/constants/locale"

interface ComponentData {
  submittedForm: boolean
  submittingForm: boolean
  emailFormShow: boolean
  trustpilotCustomers: string
  showFurnitureItems: boolean | null
  initInventory: boolean
  itemCategories: any
  hasExtensionState: boolean
  trustpilotRating: number
}

export default defineComponent({
  name: "LocationForm",

  props: {
    categoryV4Id: { type: Number, default: 0 },
    isCtaForm: { type: Boolean },
    hasExtension: { type: Boolean },
    block: { type: Object as () => Block, required: true }
  },

  data(): ComponentData {
    return {
      submittedForm: false,
      submittingForm: false,
      emailFormShow: false,
      showFurnitureItems: null,
      initInventory: false,
      itemCategories: null,
      hasExtensionState: this.hasExtension,
      trustpilotCustomers: "0",
      trustpilotRating: 4.5
    }
  },
  computed: {
    ...mapGetters("pages", {
      getWindowWidth: GET_WINDOW_WIDTH,
      isSmallOrMedium: GET_IS_SMALL_OR_MEDIUM,
      getInventoryDesktop: GET_INVENTORY_DESKTOP
    }),
    ...mapGetters("listing", {
      getListing: GET_LISTING,
      getItemsCount: GET_ITEMS_COUNT
    }),
    ...mapGetters("phone-number", {
      getPhoneNumberValue: GET_PHONE_NUMBER_VALUE
    }),
    formSchema() {
      const locationFormSchema: Record<string, any> = {
        pickupLocation: string().required(),
        deliveryLocation: string().required()
      }
      if (this.hasItemIdentifier || this.hasVoiceToText) {
        locationFormSchema["itemIdentifier"] = string().required()
      }
      return toTypedSchema(object(locationFormSchema))
    },

    isLargeOrAbove(): any {
      return !this.isSmallOrMedium
    },

    locationBlock(): any {
      return this.categoryV4Id ? this.block.innerBlocks[0] : this.block.innerBlocks[1]
    },

    ctaBlock(): any {
      return this.block.innerBlocks[this.block.innerBlocks.length - 1]
    },

    ctaFormButtons(): any[] {
      if (this.isCtaForm) {
        return this.block.innerBlocks[0].innerBlocks[0].innerBlocks[0].innerBlocks
      }
      return []
    },

    ctaFormHasMultipleButtons(): boolean {
      if (this.isCtaForm) {
        return this.ctaFormButtons.length > 1
      }
      return false
    },

    getInventoryComputed(): boolean {
      return (
        (this.isSmallOrMedium || this.hasExtensionState) &&
        !this.hasItemIdentifier &&
        !this.hasVoiceToText
      )
    },

    hasAvailabilityRemark(): boolean {
      return patternCheck(this.block.attributes, AVAILABILITY_REMARK)
    },

    hasItemIdentifier(): boolean {
      return patternCheck(this.block.attributes, ITEM_IDENTIFIER)
    },

    hasVoiceToText(): boolean {
      return patternCheck(this.block.attributes, VOICE_TO_TEXT)
    },

    hasCallUs(): boolean {
      return patternCheck(this.block.attributes, CALL_US)
    },

    hasRetrieveQuote(): boolean {
      return true
    },

    isValidForm(): boolean {
      const listing = this.getListing
      if (!isValidLocation(listing?.pickup) || !isValidLocation(listing?.delivery)) {
        return false
      }

      return true
    },

    hasItemsDesktop(): boolean {
      return this.isLargeOrAbove && this.hasExtensionState && this.getItemsCount
    },

    formattedTrustpilotCustomers(): string {
      return formatNumberWithLocale(this.trustpilotCustomers, this.$i18n.locale)
    },
    formattedTrustpilotRating(): string {
      return formatNumberWithLocale(this.trustpilotRating, this.$i18n.locale)
    },
    locale(): string {
      return (this.$i18n && this.$i18n.locale) || 'en'
    },
    formItemClass() {
      const hasFooter = this.$i18n.locale === 'es';
      return hasFooter ? this.$style.formItemWithFooter : this.$style.formItemNoFooter;
    },
  },

  watch: {
    onSubmittingFormChanges(val) {
      if (val) {
        this.$emitter.emit("submitting-location-form")
      } else {
        this.$emitter.emit("submitted-location-form")
      }
    }
  },

  async created() {
    this.$store.dispatch("pages/" + SET_INVENTORY, null)
    const trustPilotConfig = this.$config.public.trustPilot
    let trustPilotId = trustPilotConfig.id
    let trustPilotAPIKey = trustPilotConfig.apiKey

    if (this.$i18n.locale !== LOCALE.UK) {
      const upperCaseLocale = this.$i18n.locale.toUpperCase()
      trustPilotId = trustPilotConfig[`id${upperCaseLocale}`]
      trustPilotAPIKey = trustPilotConfig[`apiKey${upperCaseLocale}`]
    }
    const trustpilotURL = `https://api.trustpilot.com/v1/business-units/${trustPilotId}`
    try {
      const axiosInstance = axios.create() as any
      const response = await axiosInstance.get(trustpilotURL, {
        params: {
          apikey: trustPilotAPIKey
        }
      })
      if (response.status === 200 && response.data) {
        this.trustpilotCustomers = response.data.numberOfReviews.total
        this.trustpilotRating = response.data.score.trustScore
      }
    } catch (err) {
      console.log("Trustpilot error", err)
    }
  },

  async mounted() {
    if (!this.initInventory) {
      this.initInventory = true
      // Safety mechanism to disable IoLP Desktop when LocationFormExtension is missing
      if (this.hasExtensionState && !this.getInventoryDesktop) {
        this.hasExtensionState = false
      }

      if (this.getInventoryComputed) {
        const itemService = new ItemService(this.$axios, this.$config.public)
        this.itemCategories = await itemService.getFurnitureDefaultInventory(null, this.$i18n.locale)
      }

      const inventoryMobile = this.getInventoryComputed && this.isSmallOrMedium
      await this.$store.dispatch("pages/" + SET_INVENTORY, inventoryMobile)

      // const inventoryDesktop = this.getInventoryComputed && !this.getIsSmallOrMedium
      // if (inventoryDesktop) {
      //   // Enable Sentry Replay tagged with Snowplow Domain UserID
      //   const { getReplay, setTag } = await import("@sentry/vue")
      //   getReplay()?.start()
      //   setTag("sp.domain_userid", this.$snowplow.getSnowplowDomainIds().domain_userid)
      // }

      const ua = navigator.userAgent.toLowerCase()
      // The implemented workaround only works on Safari & Chrome with iOS 16 & iOS 17
      if (
        ua.includes("safari") &&
        navigator.maxTouchPoints > 2 &&
        (ua.includes("version/16") ||
          ua.includes("version/17") ||
          ua.includes("iphone os 16_") ||
          ua.includes("iphone os 17_"))
      ) {
        if (ua.includes("chrome")) {
          document.body.classList.add("chrome")
        } else {
          document.body.classList.add("safari")
        }
      }
    }
  },

  beforeMount() {
    this.$emitter.on("submit-location-form", this.submitForm)
    if (!this.initInventory) {
      window.addEventListener("resize", () => {
        this.setMobileOrDesktop()
      })
    }
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.setMobileOrDesktop)
    this.$emitter.off("submit-location-form")
  },

  methods: {
    ...mapActions("listing", {
      setPickupPropertyType: SET_PICKUP_PROPERTY_TYPE,
      setDeliveryPropertyType: SET_DELIVERY_PROPERTY_TYPE
    }),

    extractHref,
    getInnerText,

    setMobileOrDesktop() {
      this.$store.dispatch("pages/" + SET_WINDOW_WIDTH, window.innerWidth)
    },

    async tryToOpenFurnitureItemsModal() {
      if (!this.itemCategories) {
        const itemService = new ItemService(this.$axios, this.$config.public)
        this.itemCategories = await itemService.getFurnitureDefaultInventory(null, this.$i18n.locale)
      }

      if (this.isValidForm) {
        this.showFurnitureItems = true
      } else {
        this.$snowplow.trackIOLPButtonDisabled(this.getItemsCount, "Form")
        await (this.$refs.form as any).validate()
      }
    },

    scrollInputToTop(el: string) {
      if (this.getWindowWidth && this.getWindowWidth > 768) {
        return
      }

      let element

      switch (el) {
        case "pickup":
          element = this.$refs.pickupInputWrapper
          break
        case "delivery":
          element = this.$refs.deliveryInputWrapper
          break
        default:
          return
      }

      if (element && element.offsetTop) {
        window.scrollTo({
          top: element.offsetTop - 25,
          behavior: "smooth"
        })
      }
    },

    async submitForm(isCalledFromPage = true) {
      if (this.submittingForm) {
        return
      }

      this.submittedForm = true
      this.submittingForm = true

      try {
        const listing = this.getListing

        const isFirstStepValid: ValidationResult = await (this.$refs.form as any).validate()

        let isValid = isFirstStepValid.valid

        const isSecondStepValid = !this.getInventoryComputed || !!this.getItemsCount
        if (isValid) {
          isValid = isSecondStepValid
        }

        if (!isValid && (this.$i18n.locale === LOCALE.UK || this.$i18n.locale === LOCALE.SPAIN)) {
          if (this.getInventoryComputed && isCalledFromPage) {
            const errors = (isFirstStepValid ? "" : "Form") + (isSecondStepValid ? "" : "Items")
            this.$snowplow.trackCTAButtonDisabled("page", this.getItemsCount, errors)
          }
          this.submittingForm = false
          this.$emit("close-dropdown")
          this.hideItems()
          return
        }

        if (!isValid && this.$i18n.locale === LOCALE.SPAIN) {
          this.submittingForm = false
          return
        }

        if (this.getInventoryComputed && isCalledFromPage) {
          this.$snowplow.trackCTAButton("page", this.getItemsCount)
        }

        const categoryV4Id = CATEGORY_V4_ID_FURNITURE

        this.$snowplow.trackButtonClick(
          getInnerText(this.ctaBlock.innerBlocks[1].innerBlocks[0].innerBlocks[0].innerHtml),
          this.$config.public.v4Host + "/" + getV4UrlByCategoryId(categoryV4Id)
        )

        if (!categoryV4Id) {
          window.location.href =
            this.$config.public.v4Host + "/" + getV4UrlByCategoryId(categoryV4Id)
          this.submittingForm = false
          return
        }

        const payload = getV4ListingPayload(listing, categoryV4Id, this.$i18n.locale)

        if (payload.v === 2) {
          redirectToV2(payload, listing, this.$config.public.v4Host)
          this.submittingForm = false
          return
        }

        const listingService = new ListingService(this.$axios, this.$config.public)

        const response: ListingResponse = await listingService.postPreListing(
          payload,
          this.$i18n.locale
        )

        if (response?.data && response.data?.pre_listing_id && response.data?.category_id) {
          if (response.is_international) {
            redirectToV2(payload, listing, this.$config.public.v4Host)
            this.submittingForm = false
            return
          }

          addPreListingIdToSessionStorage(response.data)
          document.addEventListener("visibilitychange", () => {
            this.submittingForm = false
            this.submittedForm = false
            this.showFurnitureItems = false
          })

          window.location.href = getV4Url(
            this.$config.public.v4Host,
            response.data,
            payload,
            this.$i18n.locale
          )
          return
        } else {
          this.submittingForm = false
          if (this.getInventoryComputed) {
            this.$snowplow.trackCTAButtonFailed(
              "page",
              this.getItemsCount,
              "PreListing creation failed"
            )
          }
        }
      } catch (err) {
        this.submittingForm = false
        if (this.getInventoryComputed) {
          this.$snowplow.trackCTAButtonFailed(
            "page",
            this.getItemsCount,
            "PreListing creation error" + err
          )
        }
        console.log("PreListing creation error", err)
      }
    },

    onEmailFormOpen() {
      this.emailFormShow = true
    },

    onEmailFormClose() {
      this.emailFormShow = false
    },

    handleCallUsClick(label = "location_form_button"): void {
      this.$snowplow.trackCallUs({
        phoneNumber: this.getPhoneNumberValue.baseRaw,
        label,
        action: "click",
        extension: this.getPhoneNumberValue.extension
      })
    },

    hideItemsOnClickOutside(event): void {
      // If the user clicked at the Summary or Create/Edit Item dialog then we should not close the dropdown.
      if (event.target.closest(".extension-block") || event.target.closest("#modalContent")) return
      this.$emitter.emit("close-dropdown")
      this.hideItems()
    },

    hideItems(): void {
      setTimeout(() => {
        this.showFurnitureItems = false
      }, 500)
    },

    // Wrapper for CTA Button Click
    onCTAClick() {
      // Track the CTA button click event
      this.$snowplow.trackEvent({
        category: "get_instant_quote",
        action: "click"
      })
    }
  }
})
</script>

<style lang="scss" module>
.formItemBase {
  @apply relative flex flex-wrap mt-ds-ml bg-white;


  // Fix the main form sections spacing
  > div[class*="order-"] {
    &.order-2,
    &.order-3,
    &.order-4,
    &.order-5 {
      @apply mb-0;
    }
  }

  :global {
    .av-column-container {
      min-width: max-content;

      .av-button {
        @apply mt-0;
        border-radius: 5px;
      }

      .av-buttons > .av-button {
        @apply mt-0 w-full text-center;
      }
    }

    // Fix button sizing to match inputs
    .av-button.rebrand-cta {
        height: 56px;
        min-height: 56px;


      // Ensure no extra padding is messing with height
      &:not(.loading) {
        @apply py-0;
      }
    }

    .call-us-description {
      @apply text-sm text-center italic mb-ds-xxxs;
    }

    .call-us-container {
      transition: 0.2s;
      &:hover {
        filter: saturate(1.5) brightness(0.9);
      }
      a {
        @apply flex justify-center items-center w-full h-ds-xxl px-ds-md py-ds-ms font-bold text-rebrandSm text-white no-underline bg-anyvan-cta-secondary border border-black/20;
        border-radius: 8px;
        line-height: normal;

        svg {
          @apply mr-ds-sm;
        }
      }
    }

    .disabled-div {
      & > div:first-child {
        @apply text-gray-600 cursor-not-allowed;
      }
    }
  }
}

@screen md {
  .formItem {
    :global {
      .call-us-description,
      .call-us-container:not(.call-us-cta) {
        @apply hidden;
      }
    }
  }
}

@screen lg {
  .formItem {
    @apply min-w-[487px];
  }
}

.formItemWithFooter {
  composes: formItemBase;
  border-radius: 6px 6px 0px 0px;
  box-shadow: none;
}

.formItemNoFooter {
  composes: formItemBase;
  border-radius: 6px;
  box-shadow:
    0 2px 6px 2px rgba(0, 0, 0, 0.15),
    0 1px 2px 0 rgba(0, 0, 0, 0.3);
}
</style>
